import React, { useEffect, useMemo, useState } from 'react';

import { NextPage } from 'next';

import { Breadcrumb, BreadcrumbWithUrl } from '@domains/breadcrumbs';
import { RecipeDetailsPageProps } from '@domains/view';

import { sendShowProductsEvent } from '@utils/analytics';
import { formatAmountWithCurrency } from '@utils/currency';
import { isValueSet } from '@utils/guards';
import { makeRecipesRoute } from '@utils/route';
import { formatCookingTime, formatNumberOrIngredients, getProductsTotalPrice } from '@utils/utils';

import { DefaultLayout } from '@layouts/DefaultLayout/DefaultLayout';

import { Breadcrumbs } from '@components/common/Breadcrumbs/Breadcrumbs';
import { Button } from '@components/common/Button/Button';
import { Container } from '@components/common/Container/Container';
import { FavoriteButton } from '@components/common/FavoriteButton/FavoriteButton';
import { ImagePreload } from '@components/common/ImagePreload/ImagePreload';
import { Picture } from '@components/common/Picture/Picture';
import { RecipesSlider } from '@components/common/RecipesSlider/RecipesSlider';
import { WysiwygPresenter } from '@components/common/WysiwygPresenter/WysiwygPresenter';
import { CartIcon, ClockIcon, DishCoverIcon } from '@components/icons';
import { ProductCard } from '@components/product/ProductCard/ProductCard';
import { BuyRecipeProductsDialog } from '@components/recipe/RecipeBuyProductsDialog/RecipeBuyProductsDialog';

import styles from './RecipeView.module.scss';

const mobileButton: BreadcrumbWithUrl = {
    url: makeRecipesRoute(),
    caption: 'Рецепты',
};

const PRODUCTS_ELEMENT_ID = 'produkty-rietsiepta';

export const RecipeView: NextPage<RecipeDetailsPageProps> = (props) => {
    const { data } = props;
    const { recipe, other_recipes } = data;
    const { seo } = recipe;

    const hasRecipeProducts = recipe.recipe_products.length > 0;
    const recipeProducts = useMemo(
        () => recipe.recipe_products.map((item) => item.product),
        [recipe.recipe_products],
    );

    const breadcrumbs: Breadcrumb[] = [
        {
            url: makeRecipesRoute(),
            caption: 'Рецепты',
        },
        {
            caption: recipe.title,
        },
    ];

    const productsTotalPrice = useMemo(
        () => formatAmountWithCurrency(getProductsTotalPrice(recipe.recipe_products)),
        [recipe],
    );

    // Buy products modal
    const [isBuyProductsDialogOpen, setBuyProductsDialogOpen] = useState(false);

    const openBuyProductsDialog = () => setBuyProductsDialogOpen(true);

    const handleBuyProductsDialogOpenChange = (isOpen: boolean) => setBuyProductsDialogOpen(isOpen);

    useEffect(() => {
        if (recipeProducts.length === 0) return;
        sendShowProductsEvent(recipeProducts);
    }, [recipeProducts]);

    return (
        <DefaultLayout {...seo}>
            <Container>
                <Breadcrumbs items={breadcrumbs} mobileButton={mobileButton} isWithHomeCrumb />
                <article className={styles.content}>
                    <h1 className={styles.title}>{recipe.title}</h1>
                    <div className={styles.topRow}>
                        <FavoriteButton
                            type="recipe"
                            id={recipe.id}
                            initialIsFavorite={recipe.is_favorite}
                            size={40}
                            className={styles.favoriteButton}
                        />
                        <div className={styles.briefInfo}>
                            <div className={styles.briefItem}>
                                <ClockIcon className={styles.clockIcon} />
                                <span>{formatCookingTime(recipe.cooking_time_minutes)}</span>
                            </div>
                            <div className={styles.briefItem}>
                                <DishCoverIcon className={styles.dishCoverIcon} />
                                <span>{formatNumberOrIngredients(recipe.ingredient_quantity)}</span>
                            </div>
                        </div>
                    </div>
                    {isValueSet(recipe.title_image) && (
                        <>
                            <ImagePreload sources={recipe.title_image} />
                            <Picture
                                priority
                                sources={recipe.title_image}
                                className={styles.primaryImage}
                                classes={{ picture: styles.primaryPicture }}
                            />
                        </>
                    )}

                    {isValueSet(recipe.title_description) && (
                        <WysiwygPresenter
                            content={recipe.title_description}
                            className={styles.description}
                        />
                    )}

                    {isValueSet(recipe.ingredient_list) && (
                        <WysiwygPresenter
                            content={recipe.ingredient_list}
                            className={styles.ingredientList}
                        />
                    )}

                    {hasRecipeProducts && (
                        <Button
                            variant="greenLight"
                            className={styles.scrollToProductListButton}
                            link={`#${PRODUCTS_ELEMENT_ID}`}
                        >
                            Товары из&nbsp;списка
                        </Button>
                    )}

                    {isValueSet(recipe.instructions) && (
                        <WysiwygPresenter content={recipe.instructions} className={styles.steps} />
                    )}
                </article>

                {hasRecipeProducts && (
                    <div className={styles.productGridHeader}>
                        <div className={styles.productGridHeaderAnchor} id={PRODUCTS_ELEMENT_ID} />
                        <span className={styles.productGridTitle}>Товары для&nbsp;рецепта</span>
                        <Button
                            variant="green"
                            icon={CartIcon}
                            classes={{ icon: styles.cartIcon }}
                            className={styles.addAllProductsToCartButton}
                            onClick={openBuyProductsDialog}
                        >
                            Добавить товары на {productsTotalPrice}
                        </Button>
                    </div>
                )}
                <div className={styles.productGrid}>
                    {recipeProducts.map((product) => (
                        <ProductCard key={product.id} product={product} />
                    ))}
                </div>
            </Container>

            {other_recipes.length > 0 && (
                <RecipesSlider
                    recipes={other_recipes}
                    title="Другие рецепты"
                    className={styles.recipesSlider}
                    classes={{ pagination: styles.recipesSliderPagination }}
                />
            )}
            <BuyRecipeProductsDialog
                isOpen={isBuyProductsDialogOpen}
                initialProducts={recipe.recipe_products}
                onOpenChange={handleBuyProductsDialogOpenChange}
            />
        </DefaultLayout>
    );
};
